/** JobsTray — app-wide async catalog-job tray (topbar).
 *
 *  Lets admins fire a bulk collection (catalog generation, HF-metadata
 *  enrichment, eval refresh) and then use the REST of the site while it runs:
 *  the tray lives in `DashboardTopbar`, polls the jobs LIST while anything is
 *  moving, and is the SINGLE owner of bulk-job completion toasts (the header
 *  chips that used to own them are gone).
 *
 *  Reuse notes: trigger mirrors the topbar Help-button styling; the panel is
 *  the canonical shadcn Popover; rows reuse Badge (+ lifted `jobStatusVariant`),
 *  the ui Progress primitive, and `formatRelativeTime`/`sentenceCase`.
 *  Admin-gated: `GET /admin/catalog/jobs` is admin-only, so the tray renders
 *  nothing (and fetches nothing) for regular users.
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ListChecks, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';
import { useIsAdmin } from '@/api/hooks/useAuth';
import { adminModelsKey, adminCatalogJobsKey, useCatalogJobs } from '@/api/hooks/useAdmin';
import type { CatalogJobSummary } from '@/api/types';
import { formatRelativeTime, sentenceCase } from '@/lib/format';
import {
  CATALOG_JOB_TERMINAL_SET,
  TOTAL_PHASES,
  jobStatusVariant,
} from '@/features/admin/models/jobStatus';

const TRAY_LIMIT = 8;

/** i18n prefix (models.json) for the two bulk-collection job types whose
 *  completion toasts the tray owns. Generation completion is deliberately
 *  excluded: its commit flow (GenerateCatalogDialog / CatalogDiffPreview)
 *  already handles its own UX. */
const TOAST_PREFIX: Record<string, string> = {
  metadata_enrichment: 'update',
  eval_refresh: 'evalUpdate',
};

/** Determinate progress for a job row; null hides the bar (failed/cancelled/
 *  expired rows show the status badge + error line instead). */
function jobProgress(job: CatalogJobSummary): number | null {
  const status = job.status?.toLowerCase();
  if (status === 'completed' || status === 'preview_ready') return 100;
  if (CATALOG_JOB_TERMINAL_SET.has(status)) return null; // failed/cancelled/expired
  const total = TOTAL_PHASES[job.job_type];
  if (!total) return null;
  return Math.max(0, Math.min(100, ((job.current_phase ?? 0) / total) * 100));
}

function JobRow({ job, onNavigate }: { job: CatalogJobSummary; onNavigate: () => void }) {
  const { t } = useTranslation('models');
  const progress = jobProgress(job);
  const status = job.status?.toLowerCase();
  const isPreviewReady = job.job_type === 'catalog_generation' && status === 'preview_ready';
  // Any generation run that is still actionable (running phases OR awaiting
  // review) deep-links back to the Models page with its job id, so the diff
  // preview / phase view is reachable from the tray — not only from the
  // dialog chain that started the run.
  const isOpenableGeneration =
    job.job_type === 'catalog_generation' &&
    (isPreviewReady || !CATALOG_JOB_TERMINAL_SET.has(status));

  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-medium">
          {t(`jobsTray.jobType.${job.job_type}`, {
            defaultValue: sentenceCase(job.job_type.replace(/_/g, ' ')),
          })}
        </span>
        <Badge variant={jobStatusVariant(job.status)} className="font-normal">
          {sentenceCase(job.status.replace(/_/g, ' '))}
        </Badge>
      </div>
      {progress != null && <Progress value={progress} className="mt-2" />}
      <p className="mt-1.5 text-xs text-muted-foreground">
        {formatRelativeTime(job.updated_at ?? job.created_at)}
      </p>
      {/* completed-with-warning also carries error_message — keep it visible */}
      {job.error_message && (
        <p
          className={
            job.status === 'failed'
              ? 'mt-0.5 truncate text-xs text-destructive'
              : 'mt-0.5 truncate text-xs text-warning'
          }
          title={job.error_message}
        >
          {job.error_message}
        </p>
      )}
      {isPreviewReady && (
        <p className="mt-0.5 text-xs text-info">{t('jobsTray.previewReady')}</p>
      )}
    </>
  );

  if (isOpenableGeneration) {
    return (
      <Link
        to={`/dashboard/admin/models?catalogJob=${job.id}`}
        onClick={onNavigate}
        className="block rounded-md px-3 py-2.5 transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
      >
        {body}
      </Link>
    );
  }
  return <div className="px-3 py-2.5">{body}</div>;
}

function JobsTrayInner() {
  const { t } = useTranslation('models');
  const { t: tErrors } = useTranslation('errors');
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const jobs = useCatalogJobs({ limit: TRAY_LIMIT }, { poll: true });
  const items = jobs.data?.items ?? [];
  const activeCount = items.filter(
    (j) =>
      !CATALOG_JOB_TERMINAL_SET.has(j.status?.toLowerCase()) &&
      j.status?.toLowerCase() !== 'preview_ready',
  ).length;

  // Terminal-transition watcher (mirrors the old BulkSyncChip effect): when a
  // job we previously saw as non-terminal reaches a terminal state, refresh
  // the catalog lists once and toast the outcome. Seeded on first load so
  // historical jobs never re-toast.
  const prevStatuses = useRef<Map<string, string> | null>(null);
  useEffect(() => {
    if (!jobs.data) return;
    const rows = jobs.data.items;
    const next = new Map(rows.map((j) => [j.id, j.status]));
    const prev = prevStatuses.current;
    prevStatuses.current = next;
    if (!prev) return; // first load — seed only

    for (const job of rows) {
      const before = prev.get(job.id);
      const wasActive = before !== undefined && !CATALOG_JOB_TERMINAL_SET.has(before);
      if (!wasActive || !CATALOG_JOB_TERMINAL_SET.has(job.status)) continue;

      qc.invalidateQueries({ queryKey: ['models'] });
      qc.invalidateQueries({ queryKey: adminModelsKey });
      qc.invalidateQueries({ queryKey: adminCatalogJobsKey });

      const prefix = TOAST_PREFIX[job.job_type];
      if (!prefix) continue;
      if (job.status === 'completed') {
        // A completed collection may still carry a loud partial-data warning
        // (e.g. leaderboard unavailable) — surface it, never swallow it.
        if (job.error_message) {
          toast.warning(job.error_message);
        } else {
          toast.success(t(`${prefix}.done`));
        }
      } else if (job.status === 'failed') {
        toast.error(job.error_message ?? tErrors('generic'));
      }
    }
  }, [jobs.data, qc, t, tErrors]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          aria-label={t('jobsTray.openLabel')}
          title={t('jobsTray.openLabel')}
          className="relative ml-2 rounded-full border-border/60 text-muted-foreground hover:border-primary/40 hover:bg-primary/5 hover:text-foreground"
        >
          <ListChecks className="h-4 w-4" />
          {activeCount > 0 && (
            <Badge
              variant="default"
              className="absolute -right-1.5 -top-1.5 h-4 min-w-4 animate-pulse px-1 text-[10px] leading-none"
              aria-label={t('jobsTray.activeCount', { count: activeCount })}
            >
              {activeCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2.5">
          <span className="text-sm font-medium">{t('jobsTray.title')}</span>
          {activeCount > 0 && (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              {t('jobsTray.activeCount', { count: activeCount })}
            </span>
          )}
        </div>
        {items.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">
            {t('jobsTray.empty')}
          </p>
        ) : (
          <ul className="max-h-96 divide-y divide-border overflow-y-auto py-1">
            {items.map((job) => (
              <li key={job.id}>
                <JobRow job={job} onNavigate={() => setOpen(false)} />
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function JobsTray() {
  // Gate BEFORE mounting the inner tray: the jobs list is an admin-only
  // endpoint, so regular users must not trigger the query at all.
  const isAdmin = useIsAdmin();
  if (!isAdmin) return null;
  return <JobsTrayInner />;
}
